/**
 * **设置页（叠加层）。**
 *
 * 评审：「设置页需要可以叠加在任意其他界面（菜单/游戏）之上」。
 * 所以它是一个 **overlay**，不是 screen —— 底下那块照常显示，
 * 由 `screenHost.ts` 的遮罩负责让它点不动。
 *
 * 内容（评审列的那几项）：**昵称 / BGM 音量 / 音效音量 / UI 大小 / 存档导出**。
 *
 * ## 三个和别处不一样的地方
 *
 * 1. **音量的滑块"边拖边生效"，UI 大小"松手才生效"。**
 *    前者只改一个数字，改完不用重排任何东西；后者会让整个界面按新倍率重建，
 *    在拖动中就重建会把正在拖的那个滑块本身销毁掉 —— 手一松就断在半路。
 *    所以 UI 大小用 `onCommit`（松手一次），其余用 `onInput`。
 *
 * 2. **导出的是"设置档"，不是对局存档。** 评审这一轮把存档搁置了，
 *    而对局记录的格式还没定（`architecture.md` §3.8 的棋谱模型）。
 *    与其编一个格式，不如先导出**此刻真实存在的东西**：这份设置。
 *    等存档格式定下来，把内容换掉即可，界面一行都不用动。
 *
 * 3. **音效目前只有 IMPACT 一记。** 它由 `audio/sfx.ts` 合成，不用素材
 *    （见 `main.ts` 的 `playImpact`）。滑块的读写、持久化、真实音量都是通的，
 *    只是除了那一记暂时没有别的音效。界面上明说这件事，免得它看起来像坏掉的控件。
 */

import { Container } from 'pixi.js';
import { NICKNAME_MAX, displayNickname, serializeSettings } from '../settings';
import { UI_SCALE_MAX, UI_SCALE_MIN } from '../../render/ui/metrics';
import {
  STAMP_GAP,
  createColumn,
  label,
  richLabel,
  pageChrome,
  panel,
  scrollArea,
  sectionTitle,
  slider,
  stampButton,
  textField,
  uiPad,
  INK,
} from '../../render/ui';
import { LANG_NAMES, LANGS, bi, t, type TextKey } from '../../i18n';
import { GAMEPAD_LAYOUTS } from '../../render/input/gamepad';
import { LORE_SECTIONS } from '../../core/lore';
import type { Screen, ScreenContext } from '../screen';
import { OVERLAY } from './ids';
import { downloadText, exportFilename } from '../transfer';

const PANEL_WIDTH = 640;
const PANEL_PADDING = 20;
const NICKNAME_FIELD_WIDTH = 300;
/** 面板距页面顶部的距离。让开页眉行（y=34）和标题 */
const PANEL_TOP = 104;
/** 提示文字挂多久消失（毫秒） */
const STATUS_MS = 2600;
/** 底部留给"关闭"按钮的高度 */
const BUTTON_RESERVE = 72;
/** 滚动区遮罩左右各多留的余量（避免裁掉控件的描边） */
const CONTENT_INSET = 8;

export function createSettingsScreen(ctx: ScreenContext): Screen {
  const container = new Container();
  container.label = 'screen-settings';
  // 铺满并吃掉点击 —— 叠加层该有的样子。真正的"挡住底下"由宿主的遮罩负责，
  // 这里再设一次是因为设置页的**空白处**也不该把点击漏给底下的菜单
  container.eventMode = 'static';

  let column = createColumn(1);
  let status: ReturnType<typeof label> | null = null;
  let statusLeft = 0;

  const setStatus = (text: string): void => {
    if (status === null) return;
    status.text = text;
    statusLeft = STATUS_MS;
  };

  const build = (): void => {
    const settings = ctx.settings.get();
    const scale = settings.uiScale;
    const { width, height } = ctx.bounds;

    column = createColumn(scale);
    container.addChild(column.container);

    // ---- 版面装置 ----
    column.addRaw(
      pageChrome({ scale, width, height, header: bi('common.settings'), page: 'P.SET' }),
    );

    // ---- 面板与内容 ----
    const sheet = panel({
      scale,
      width: PANEL_WIDTH,
      title: bi('common.settings'),
      padding: PANEL_PADDING,
    });
    sheet.container.position.set(Math.round((width - PANEL_WIDTH) / 2), uiPad(PANEL_TOP, scale));
    column.addRaw(sheet.container);

    // 内容装进**滚动区**：设置项只会越加越多，靠"算高度去挤"迟早被多出来的一行
    // 顶爆（见 `render/ui/scroll.ts` 的说明）。滚动区高度固定，关闭键永远留在屏幕内。
    const contentWidth = PANEL_WIDTH - uiPad(PANEL_PADDING * 2 + CONTENT_INSET, scale);
    const overhead = uiPad(PANEL_TOP + 24 + PANEL_PADDING * 2 + BUTTON_RESERVE, scale);
    const area = scrollArea({
      scale,
      width: contentWidth,
      bleed: uiPad(CONTENT_INSET, scale),
      height: Math.max(uiPad(180, scale), height - overhead),
    });
    sheet.body.addChild(area.container);

    const inner = createColumn(scale, 14);
    area.content.addChild(inner.container);

    // ---------------- 身份 ----------------
    inner.add(sectionTitle({ text: bi('common.identity'), scale, width: contentWidth }), 0, uiPad(2, scale));

    const nicknameRow = new Container();
    const nicknameName = richLabel({ text: t('common.nickname'), scale, size: 13, fill: INK.text, role: 'cjk' });
    const nicknameField = textField({
      scale,
      width: NICKNAME_FIELD_WIDTH,
      keys: ctx.keys,
      maxLength: NICKNAME_MAX,
      placeholder: displayNickname(settings),
      value: settings.nickname,
      onChange: (value) => ctx.settings.patch({ nickname: value }),
      onSubmit: () => nicknameField.blur(),
    });
    nicknameName.position.set(0, Math.round((nicknameField.height - nicknameName.height) / 2));
    nicknameField.container.position.set(contentWidth - NICKNAME_FIELD_WIDTH, 0);
    nicknameRow.addChild(nicknameName, nicknameField.container);
    inner.add({ container: nicknameRow, width: contentWidth, height: nicknameField.height });
    inner.track(() => nicknameField.blur());

    // ---------------- 语言 ----------------
    inner.add(sectionTitle({ text: bi('settings.section.language'), scale, width: contentWidth }), 0, uiPad(8, scale));
    const langRow = new Container();
    let langX = 0;
    let langRowHeight = 0;
    for (const lang of LANGS) {
      const button = stampButton({
        label: LANG_NAMES[lang],
        scale,
        // 当前语言用实心章，其余是描边章
        variant: settings.lang === lang ? 'primary' : 'secondary',
        onPress: () => ctx.settings.patch({ lang }),
      });
      button.container.position.set(langX, 0);
      langX += button.width + uiPad(STAMP_GAP, scale);
      langRowHeight = Math.max(langRowHeight, button.height);
      langRow.addChild(button.container);
    }
    inner.add({ container: langRow, width: contentWidth, height: langRowHeight });
    inner.add(
      richLabel({
        text: t('settings.languageNote'),
        scale,
        size: 11,
        fill: INK.textDim,
        role: 'cjk',
        wrapWidth: contentWidth,
      }),
      0,
      uiPad(2, scale),
    );


    // ---------------- 手柄（只有这一趟会话里见过手柄连接才显示）----------------
    // 「见过」是闩住的（见 `render/input/traits.ts` 的 gamepadSeen）：插过一次之后，
    // 哪怕暂时拔掉，玩家也还能回来改布局 —— 不显示的话那个设置就永远找不到了。
    if (ctx.traits().gamepadSeen) {
      inner.add(sectionTitle({ text: bi('settings.section.gamepad'), scale, width: contentWidth }), 0, uiPad(8, scale));
      const padRow = new Container();
      let padX = 0;
      let padRowHeight = 0;
      for (const layout of GAMEPAD_LAYOUTS) {
        const button = stampButton({
          label: t(layout === 'nintendo' ? 'settings.gamepadNintendo' : 'settings.gamepadXbox'),
          scale,
          variant: settings.gamepadLayout === layout ? 'primary' : 'secondary',
          onPress: () => ctx.settings.patch({ gamepadLayout: layout }),
        });
        button.container.position.set(padX, 0);
        padX += button.width + uiPad(STAMP_GAP, scale);
        padRowHeight = Math.max(padRowHeight, button.height);
        padRow.addChild(button.container);
      }
      inner.add({ container: padRow, width: contentWidth, height: padRowHeight });
      inner.add(
        richLabel({
          text: t('settings.gamepadNote'),
          scale,
          size: 11,
          fill: INK.textDim,
          role: 'cjk',
          wrapWidth: contentWidth,
        }),
        0,
        uiPad(2, scale),
      );
    }

    // ---------------- 声音 ----------------
    inner.add(sectionTitle({ text: bi('settings.section.audio'), scale, width: contentWidth }), 0, uiPad(8, scale));

    const bgm = slider({
      label: t('settings.bgmVolume'),
      scale,
      min: 0,
      max: 1,
      step: 0.05,
      value: settings.bgmVolume,
      width: contentWidth,
      input: ctx.input,
      format: (v) => `${Math.round(v * 100)}%`,
      onInput: (value) => ctx.settings.patch({ bgmVolume: value }),
    });
    inner.add(bgm);
    inner.track(() => bgm.destroy());

    const sfx = slider({
      label: t('settings.sfxVolume'),
      scale,
      min: 0,
      max: 1,
      step: 0.05,
      value: settings.sfxVolume,
      width: contentWidth,
      input: ctx.input,
      format: (v) => `${Math.round(v * 100)}%`,
      onInput: (value) => ctx.settings.patch({ sfxVolume: value }),
    });
    inner.add(sfx);
    inner.track(() => sfx.destroy());

    // ---------------- 界面 ----------------
    inner.add(sectionTitle({ text: bi('settings.section.interface'), scale, width: contentWidth }), 0, uiPad(8, scale));

    const uiScale = slider({
      label: t('settings.uiScale'),
      scale,
      min: UI_SCALE_MIN,
      max: UI_SCALE_MAX,
      step: 0.05,
      value: settings.uiScale,
      width: contentWidth,
      input: ctx.input,
      format: (v) => `${Math.round(v * 100)}%`,
      // ⚠️ 松手才生效，见文件头第 1 条
      onCommit: (value) => ctx.settings.patch({ uiScale: value }),
    });
    inner.add(uiScale);
    inner.track(() => uiScale.destroy());
    inner.add(
      richLabel({
        text: t('settings.uiScaleNote'),
        scale,
        size: 11,
        fill: INK.textDim,
        role: 'cjk',
        wrapWidth: contentWidth,
      }),
      0,
      uiPad(2, scale),
    );

    // ---------------- 卡牌说明 ----------------
    inner.add(sectionTitle({ text: bi('settings.section.lore'), scale, width: contentWidth }), 0, uiPad(8, scale));

    // 5 个板块排成一行（和语言选择同一种印章选择器）：设置页已经很长，
    // 竖排 5 行开关会把面板顶出屏幕；这里用一排小章，选中的是实心章。
    const loreRow = new Container();
    const loreButtons: { section: (typeof LORE_SECTIONS)[number]; button: ReturnType<typeof stampButton> }[] = [];
    let loreX = 0;
    let loreRowHeight = 0;
    for (const section of LORE_SECTIONS) {
      const on = settings.loreSections.includes(section);
      const button = stampButton({
        label: t(('lore.section.' + section) as TextKey),
        scale,
        variant: on ? 'primary' : 'secondary',
        onPress: () => {
          const current = ctx.settings.get().loreSections;
          // 保持 `LORE_SECTIONS` 的顺序：显示顺序就是这个数组的顺序
          const next = current.includes(section)
            ? current.filter((s) => s !== section)
            : LORE_SECTIONS.filter((s) => s === section || current.includes(s));
          ctx.settings.patch({ loreSections: next });
          // ⚠️ 变体是构造时定死的，宿主也**不会**为 loreSections 重建整屏
          //    （见 `screenHost` 的订阅）。改完必须自己把这颗按钮重画一遍，
          //    否则状态变了、按钮看起来没反应。
          button.setVariant(next.includes(section) ? 'primary' : 'secondary');
        },
      });
      loreButtons.push({ section, button });
      button.container.position.set(loreX, 0);
      loreX += button.width + uiPad(STAMP_GAP, scale);
      loreRowHeight = Math.max(loreRowHeight, button.height);
      loreRow.addChild(button.container);
    }
    /** 外部改了 `loreSections`（比如「恢复默认」）之后，把这一排按钮对齐到当前状态 */
    function syncLore(): void {
      const selected = ctx.settings.get().loreSections;
      for (const entry of loreButtons) {
        entry.button.setVariant(selected.includes(entry.section) ? 'primary' : 'secondary');
      }
    }
    inner.add({ container: loreRow, width: contentWidth, height: loreRowHeight });
    inner.add(
      richLabel({
        text: t('settings.loreNote'),
        scale,
        size: 11,
        fill: INK.textDim,
        role: 'cjk',
        wrapWidth: contentWidth,
      }),
      0,
      uiPad(2, scale),
    );

    // ---------------- 存档 ----------------
    inner.add(sectionTitle({ text: bi('settings.section.save'), scale, width: contentWidth }), 0, uiPad(8, scale));

    const exportButton = stampButton({
      label: t('settings.export'),
      scale,
      variant: 'secondary',
      onPress: () => {
        const result = downloadText(
          exportFilename('settings', new Date()),
          serializeSettings(ctx.settings.get()),
        );
        setStatus(
          result.ok
            ? t('settings.exported')
            : t('settings.exportFailed', { reason: result.reason ?? t('common.unknownReason') }),
        );
      },
    });
    const resetButton = stampButton({
      label: t('common.resetDefaults'),
      scale,
      variant: 'quiet',
      onPress: () => {
        ctx.settings.reset();
        // 恢复默认也会改 loreSections：那一排按钮要跟着回来
        syncLore();
        setStatus(t('settings.resetDone'));
      },
    });
    resetButton.container.position.set(exportButton.width + uiPad(STAMP_GAP, scale), 0);
    const saveRow = new Container();
    saveRow.addChild(exportButton.container, resetButton.container);
    inner.add({ container: saveRow, width: contentWidth, height: exportButton.height });

    status = richLabel({ text: '', scale, size: 11, fill: INK.ok, role: 'cjk' });
    inner.add(status);

    // ---- 面板高度由内容决定，然后才把"关闭"摆在它下面 ----
    // ⚠️ 只报"内容多高"，标题栏和内边距由 `fitContent` 自己加（见它的说明）
    area.setContentHeight(Math.round(inner.y));
    sheet.fitContent(area.height);
    const panelHeight = sheet.height;

    const closeButton = stampButton({
      label: t('common.close'),
      scale,
      variant: 'primary',
      onPress: () => ctx.stack.closeOverlay(OVERLAY.settings),
    });
    closeButton.container.position.set(
      Math.round((width - closeButton.width) / 2),
      uiPad(PANEL_TOP, scale) + panelHeight + uiPad(16, scale),
    );
    column.addRaw(closeButton.container);
  };

  build();

  return {
    container,
    rebuild() {
      // ⚠️ 顺序要紧：先摘掉滑块挂在舞台上的监听器，再销毁容器
      column.dispose();
      container.removeChildren();
      status = null;
      build();
    },
    onKey(event) {
      if (event.key !== 'Escape') return false;
      ctx.stack.closeOverlay(OVERLAY.settings);
      return true;
    },
    update(deltaMS) {
      if (statusLeft <= 0 || status === null) return;
      statusLeft -= deltaMS;
      if (statusLeft <= 0) status.text = '';
    },
    destroy() {
      column.dispose();
      container.destroy({ children: true });
    },
  };
}
